// Admin → Import: the PRODUCTION CHECK OFF workbook matched against the open source, as a plan the
// page shows before anything is written. Pure: the backend reads the file (`backend/src/routing/
// workbook.ts`, exceljs) and gives each tab's rows as cells by field; everything else is here, so the
// page, the route and `scripts/import-plan-check.ts` count the same way.
//
// The file is typed by hand. A row is matched by the numbers in its PO / PT cell: every 7-digit
// `1[6-9]xxxxx` and every range of them, kept only when open in A2000, and every `SO` number, kept
// only when open in Deposco — a PO that happens to be 7 digits falls out by "open", never by a regex
// trick. The WMS cell counts only when the cell names no open pick ticket or SO and no other row names
// that WMS's. Source fields (customer, dates, QTY, PO / PT, WMS) never come from the file.

import { ROUTING_LINES } from "../entities";
import type { CarrierOption } from "./carriers";
import type { LineSource } from "./deposco";
import { deposcoSourceRow } from "./deposco";
import type { DeposcoRow } from "./deposco";
import type { LineView } from "./assemble";
import { lineRef } from "./assemble";
import { approvalPlan } from "./approve";
import { customerSpelling } from "./excel";
import type { ListName } from "../entities";
import { FIELD_LIST, looksAlike, sameListValue } from "./lists";
import { PE_ASAP, parseEstimate } from "./pe-order";
import type { ListValueRecord, PickupRecord, ShippingRowRecord, WarehouseField } from "./types";

/** A cell as the file gives it: text, a number, a tick box, a date (an Excel date or time), or empty. */
export type ImportCell = string | number | boolean | Date | null;

/** What a tab is, by its headers: the PE / RS check-off, HT & SM LOG, PE / RS ORDERS, the Shipping Schedule. */
export const IMPORT_TAB_KINDS = ["production", "htsm", "pe-orders", "schedule"] as const;
export type ImportTabKind = (typeof IMPORT_TAB_KINDS)[number];

export const IMPORT_KIND_LABELS: Record<ImportTabKind, string> = {
  production: "Production check-off",
  htsm: "HT & SM log",
  "pe-orders": "PE Orders",
  schedule: "Shipping schedule",
};

/** A header with its spelling taken out: case, spaces and punctuation ("PO / PT #" → "popt"). */
export const headerKey = (h: unknown) =>
  String(h ?? "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "");

/**
 * Each kind's columns: field → the headers that are it (spellings taken out). `required` must all be
 * in a tab's first row for the tab to be that kind; the rest are read when present. The tab's name
 * never decides (tabs get renamed and padded).
 */
const KINDS: Record<ImportTabKind, { required: readonly string[]; fields: Record<string, readonly string[]> }> = {
  production: {
    required: ["customer", "poPt", "qty", "productionNotes"],
    fields: {
      customer: ["customer"],
      poPt: ["popt"],
      qty: ["qty"],
      productionNotes: ["productionnotes"],
      division: ["division"],
      received: ["receiveddate"],
      start: ["firstshipdate", "startdate"],
      cancel: ["canceldate"],
      ready: ["readydate"],
      workNeeded: ["workneeded"],
      wms: ["wms"],
      routingNotes: ["routingnotes"],
      routingRef: ["routingreference", "routingrefs"],
      schedShip: ["scheduledshipdate", "scheduleshipdate"],
      inventoryNotes: ["inventorynotes"],
      lead: ["lead"],
      timing: ["timmingforrework", "timmingforworkorder"],
      inv: ["inv"],
    },
  },
  htsm: {
    required: ["customer", "poPt", "qty", "wms"],
    fields: {
      customer: ["customer"],
      poPt: ["ptpo"],
      qty: ["qty"],
      wms: ["wms"],
      received: ["received"],
      start: ["start"],
      cancel: ["cancel"],
      workNeeded: ["workneeded"],
      cartonLabels: ["cartonlabelssent"],
      productionNotes: ["productionnotes"],
      schedShip: ["scheduledshipdate"],
      routingStatus: ["routingstatus"],
      routingRef: ["routingconf"],
      notes: ["notes"],
    },
  },
  "pe-orders": {
    required: ["customer", "poPt", "qty", "assignedTo", "status"],
    fields: {
      customer: ["customer"],
      poPt: ["popt"],
      qty: ["qty"],
      assignedTo: ["assignedto"],
      status: ["status"],
      division: ["division"],
      received: ["received"],
      start: ["startdate"],
      cancel: ["canceldate"],
      shipDate: ["shipdate"],
      pullGroup: ["pullgroup"],
      locQty: ["locqty"],
      startTime: ["starttime"],
      endTime: ["endtime"],
      pallets: ["pallets"],
      comments: ["comments"],
    },
  },
  schedule: {
    required: ["date", "customer", "po", "carrier"],
    fields: {
      date: ["date"],
      customer: ["customer"],
      po: ["po"],
      carrier: ["carrier"],
      co: ["co"],
      ref: ["ptso"],
      ctns: ["ctns"],
      plts: ["plts"],
      loadRef: ["pickupshipmentbol"],
      appTime: ["apptime"],
      loader: ["loader"],
      inTime: ["in"],
      outTime: ["out"],
      trailer: ["trailer"],
      door: ["door"],
      wmsAsnTrip: ["wmsasntrip"],
      notes: ["notes"],
    },
  },
};

/** The headerless column right after INV. on the check-off: notes (`notes`, the app's "INV." column). */
export const AFTER_INV = "afterInv";

/** A tab's kind and where each field is (0-based), from its first row; null when it is no kind. */
export function tabKindOf(
  headers: readonly ImportCell[],
): { kind: ImportTabKind; columns: Record<string, number> } | null {
  const keys = headers.map(headerKey);
  for (const kind of IMPORT_TAB_KINDS) {
    const { required, fields } = KINDS[kind];
    const columns: Record<string, number> = {};
    for (const [field, names] of Object.entries(fields)) {
      const at = keys.findIndex((k) => names.includes(k));
      if (at >= 0) columns[field] = at;
    }
    if (!required.every((f) => f in columns)) continue;
    if (kind === "production" && columns.inv !== undefined && !keys[columns.inv + 1])
      columns[AFTER_INV] = columns.inv + 1;
    return { kind, columns };
  }
  return null;
}

/** One row of a tab: its Excel row number and its cells by field. */
export interface ImportRow {
  n: number;
  cells: Record<string, ImportCell>;
}

/** A tab as read: every tab is listed; only a known kind's rows are kept. */
export interface ParsedTab {
  name: string;
  hidden: boolean;
  kind: ImportTabKind | null;
  /** Rows below the header with any value. */
  rowCount: number;
  rows: ImportRow[];
}

/** A row's cells by field, from its values (0-based, as `tabKindOf` placed them). */
export function importRow(n: number, values: readonly ImportCell[], columns: Record<string, number>): ImportRow {
  const cells: Record<string, ImportCell> = {};
  for (const [field, at] of Object.entries(columns)) {
    const v = values[at];
    if (v !== null && v !== undefined && v !== "") cells[field] = v;
  }
  return { n, cells };
}

/** Whether a tab is ticked at first: a known kind, shown, not RS's, not History's Shipped Orders. */
export const tickedAtFirst = (t: Pick<ParsedTab, "name" | "hidden" | "kind">) =>
  !!t.kind && !t.hidden && !/^\s*rs\b/i.test(t.name) && !isShippedTab(t);

/**
 * History already holds the old workbook's Shipped Orders tab (`excel.ts`): never imported. By its
 * name — its headers are the check-off's — and listed, so a renamed one shows as offered.
 */
export const isShippedTab = (t: Pick<ParsedTab, "name">) => /^\s*shipped orders/i.test(t.name);
export const SHIPPED_NOTE = "Not imported (Shipped Orders — History already holds it)";

// ── Cells ─────────────────────────────────────────────────────────────────────────────────────

/** A cell as text, trimmed; a number without a decimal tail; a date as YYYY-MM-DD. */
export function cellText(c: ImportCell | undefined): string {
  if (c === null || c === undefined) return "";
  if (c instanceof Date) return dayOf(c);
  if (typeof c === "number") return String(c);
  if (typeof c === "boolean") return c ? "TRUE" : "FALSE";
  return c.replace(/\s+/g, " ").trim();
}

const pad = (n: number) => String(n).padStart(2, "0");
/** A date cell's day: Excel's dates arrive as UTC midnight of the day typed. */
const dayOf = (d: Date) => `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;

/** An Excel serial (days since 1899-12-30) as a day; null outside 1955–2118. */
export function serialDay(n: number): string | null {
  if (!Number.isFinite(n) || n < 20000 || n > 80000) return null;
  return dayOf(new Date(Date.UTC(1899, 11, 30) + Math.floor(n) * 86_400_000));
}

const validDay = (y: number, m: number, d: number) => {
  if (m < 1 || m > 12 || d < 1 || d > 31) return null;
  const date = new Date(Date.UTC(y, m - 1, d));
  return date.getUTCMonth() === m - 1 ? dayOf(date) : null;
};
const fullYear = (y: string | undefined, year: number) => (!y ? year : y.length === 2 ? 2000 + Number(y) : Number(y));

/** A time-only cell (Excel's 1899-12-30 day): no date. */
const isTimeOnly = (d: Date) => d.getUTCFullYear() < 1901;

/**
 * A date cell's day: a date, an Excel serial, or text that is only a date — `10/29` takes `year`
 * (the row's), `10/29/26` and `10/29/2026` their own, `2026-10-29` as is. Null for anything else.
 */
export function cellDay(c: ImportCell | undefined, year: number): string | null {
  if (c instanceof Date) return isTimeOnly(c) ? null : dayOf(c);
  if (typeof c === "number") return serialDay(c);
  if (typeof c !== "string") return null;
  const t = c.trim();
  const iso = /^(\d{4})-(\d{2})-(\d{2})(?:[T ].*)?$/.exec(t);
  if (iso) return validDay(Number(iso[1]), Number(iso[2]), Number(iso[3]));
  const us = /^(\d{1,2})\/(\d{1,2})(?:\/(\d{4}|\d{2}))?$/.exec(t);
  return us ? validDay(fullYear(us[3], year), Number(us[1]), Number(us[2])) : null;
}

/** Every date written in a text (`10/15, 10/16 & 10/19`, `9/8/2026-9/11/2026`), in order, each once. */
export function datesInText(text: string, year: number): string[] {
  const out: string[] = [];
  for (const m of text.matchAll(/(?<![\d/])(\d{1,2})\/(\d{1,2})(?:\/(\d{4}|\d{2}))?(?![\d/])/g)) {
    const d = validDay(fullYear(m[3], year), Number(m[1]), Number(m[2]));
    if (d && !out.includes(d)) out.push(d);
  }
  return out;
}

/** A number cell, or text that is one ("1,200"); null otherwise. */
export function cellNumber(c: ImportCell | undefined): number | null {
  if (typeof c === "number") return Number.isFinite(c) ? c : null;
  if (typeof c !== "string") return null;
  const t = c.replace(/,/g, "").trim();
  return /^-?\d+(\.\d+)?$/.test(t) ? Number(t) : null;
}

/** The year a row's short dates take: its received date's, else `fallback`. */
export const rowYear = (row: ImportRow, fallback: number) => {
  const d = cellDay(row.cells.received, fallback);
  return d ? Number(d.slice(0, 4)) : fallback;
};

// ── The PO / PT cell ─────────────────────────────────────────────────────────────────────────

/** A range wider than this is a typo, not pick tickets: ignored and reported. */
export const MAX_RANGE = 2000;

export interface PoPtCell {
  /** The 7-digit pick ticket candidates, ranges expanded, in order, each once. */
  pts: string[];
  /** The SO numbers (`SO57892`), spaces taken out. */
  sos: string[];
  /** Ranges over `MAX_RANGE` (or backwards), as written: not expanded. */
  tooWide: string[];
}

const PT = String.raw`1[6-9]\d{5}`;
const RANGE = new RegExp(String.raw`(?<!\d)(${PT})\s*(?:-{1,2}|–|—)\s*(${PT})(?!\d)`, "g");
const SINGLE = new RegExp(String.raw`(?<!\d)${PT}(?!\d)`, "g");
const SO = /\bSO\s*#?\s*(\d{5,7})(?!\d)/gi;

/**
 * The numbers a PO / PT cell names: `4 P.Os PT. 1802506-1802509/ 580226201` → 1802506 … 1802509;
 * `8 POS / PT 1645239 1645246` → two; `2 P.O's P.T#(1632295 -- 1632296)` → a range; `SO 57892` →
 * SO57892. Which of them are pick tickets is the source's to say (`planImport`).
 */
export function parsePoPt(cell: ImportCell | undefined): PoPtCell {
  const text = cellText(cell);
  const pts: string[] = [];
  const tooWide: string[] = [];
  const add = (n: string) => void (pts.includes(n) || pts.push(n));
  let rest = text;
  for (const m of text.matchAll(RANGE)) {
    const a = Number(m[1]);
    const b = Number(m[2]);
    if (b < a || b - a > MAX_RANGE) tooWide.push(m[0]);
    else for (let n = a; n <= b; n++) add(String(n));
    rest = rest.replace(m[0], " ");
  }
  for (const m of rest.matchAll(SINGLE)) add(m[0]);
  const sos = [...new Set([...text.matchAll(SO)].map((m) => `SO${m[1]}`))];
  return { pts, sos, tooWide };
}

/** The WMS #s a WMS cell names (`37092`, `25965-25972` → both ends). */
export const wmsNumbers = (cell: ImportCell | undefined) => [
  ...new Set([...cellText(cell).matchAll(/(?<!\d)\d{4,6}(?!\d)/g)].map((m) => m[0])),
];

// ── Routing status in words ──────────────────────────────────────────────────────────────────

export interface RoutingGuess {
  status: "Routed" | "Pre-routed";
  /** The day it says, YYYY-MM-DD; null when it names none. */
  on: string | null;
}

/**
 * The routing status a Routing Notes or Routing Status cell says: `ROUTED 10/18/2026`, `Routed 1/2`
 * → Routed on that day (`year` for a short date); `PRE ROUTED 10/22`, `Prerouted 2/27` → Pre-routed;
 * a date cell alone → Routed on it. Null for anything else ("NOT ROUTED" too).
 */
export function routingFromText(cell: ImportCell | undefined, year: number): RoutingGuess | null {
  if (cell instanceof Date || typeof cell === "number") {
    const on = cellDay(cell, year);
    return on ? { status: "Routed", on } : null;
  }
  const text = cellText(cell);
  if (!text) return null;
  const only = cellDay(text, year);
  if (only) return { status: "Routed", on: only };
  const upper = text.toUpperCase();
  if (/\bNOT\s+(PRE[\s-]*)?ROUTED\b/.test(upper)) return null;
  const pre = /\bPRE[\s-]*ROUTED\b/.exec(upper);
  if (pre) return { status: "Pre-routed", on: datesInText(text.slice(pre.index), year)[0] ?? null };
  const routed = /\bROUTED\b/.exec(upper);
  if (routed) return { status: "Routed", on: datesInText(text.slice(routed.index), year)[0] ?? null };
  return null;
}

// ── What the plan is matched against ─────────────────────────────────────────────────────────

/** An open pick ticket or SO the app holds, and the line it is on. */
export interface ImportRef {
  ref: string;
  source: LineSource;
  /** Its line's key (`WMS|…`, `DEP|…`); "" while it waits (no WMS # yet, a Deposco SO waiting for a group). */
  line: string;
  wait: "" | "no-wms" | "group";
  qty: number | null;
  start: string | null;
  cancel: string | null;
  customerCode: string;
  customerName: string;
  wh: string;
  po: string;
}

/** A line the plan may write: its key, how the team names it, and whether it has a record already. */
export interface ImportLine {
  key: string;
  /** Its record, when it has one. */
  id: string | null;
  source: LineSource;
  /** "WMS 37092", "PO 50264439 · DC 699". */
  place: string;
  wms: string;
  stored: boolean;
  finished: boolean;
  /** The pick-ticket copies its first save writes (`approvalPlan`: none while the baseline fits). */
  copies: number;
  refs: string[];
  /** Its customer as the source names it, and its Deposco status (Step 3's ambiguous POs). */
  customer: string;
  status: string;
}

export interface ImportSource {
  refs: ImportRef[];
  lines: ImportLine[];
  pickups: readonly PickupRecord[];
  shippingRows: readonly ShippingRowRecord[];
  listValues: readonly ListValueRecord[];
  carriers: readonly CarrierOption[];
  /** When A2000 and Deposco were last loaded (ISO), as the Worklist says. */
  loadedAt: { a2000: string | null; deposco: string | null };
}

/**
 * The open pick tickets and SOs, and their lines, from the Worklist's own lines (`assemble`) and the
 * Deposco SOs waiting for a group (`withDeposco`'s `waiting`).
 */
export function importSource(
  lines: readonly LineView[],
  waiting: readonly DeposcoRow[],
  rest: Omit<ImportSource, "refs" | "lines">,
): ImportSource {
  const refs: ImportRef[] = [];
  const out: ImportLine[] = [];
  for (const l of lines) {
    const live = l.pts.filter((p) => p.source);
    if (!live.length) continue;
    const noWms = l.noWms;
    for (const p of live)
      refs.push({
        ref: p.pickTicket,
        source: l.source,
        line: noWms ? "" : l.groupKey,
        wait: noWms ? "no-wms" : "",
        qty: p.qty,
        start: p.start,
        cancel: p.cancel,
        customerCode: p.customerCode,
        customerName: p.customerName,
        wh: p.wh,
        po: p.customerPo,
      });
    if (noWms) continue;
    out.push({
      key: l.groupKey,
      id: l.stored?.id ?? null,
      source: l.source,
      place: lineRef(l) || l.groupKey,
      wms: l.wmsNo,
      stored: !!l.stored,
      finished: !!l.finished,
      copies: l.stored ? 0 : approvalPlan(l, "", () => null).writes.length,
      refs: live.map((p) => p.pickTicket),
      customer: l.customerName,
      status: l.deposco?.status?.status ?? "",
    });
  }
  for (const r of waiting) {
    const s = deposcoSourceRow(r);
    refs.push({
      ref: String(s.pick_ticket),
      source: "Deposco",
      line: "",
      wait: "group",
      qty: s.qty,
      start: s.start_date,
      cancel: s.cancel_date,
      customerCode: s.customer_code ?? "",
      customerName: s.customer_name ?? "",
      wh: s.wh ?? "",
      po: s.customer_po ?? "",
    });
  }
  return { ...rest, refs, lines: out };
}

// ── The plan ─────────────────────────────────────────────────────────────────────────────────

export const ROW_OUTCOMES = [
  "import",
  "import-split",
  "partial",
  "conflict",
  "ambiguous",
  "exists",
  "dropped",
  "error",
] as const;
export type RowOutcome = (typeof ROW_OUTCOMES)[number];

export const OUTCOME_LABELS: Record<RowOutcome, string> = {
  import: "Imports",
  "import-split": "Splits over several lines",
  partial: "Some numbers gone",
  conflict: "Conflict",
  ambiguous: "PO on several Deposco lines",
  exists: "Line already saved",
  dropped: "Dropped",
  error: "Can't read",
};

/** Why a row with no line is dropped. */
export type DropReason = "closed" | "no-number" | "no-wms" | "group";
export const DROP_LABELS: Record<DropReason, string> = {
  closed: "No open pick ticket or SO",
  "no-number": "Names no pick ticket or SO",
  "no-wms": "Open, no WMS # yet",
  group: "Open Deposco SO waiting for a group",
};

/** A difference between the row and the source: what the file says and what the app has. */
export interface ImportDiff {
  field: "QTY" | "FIRST SHIP" | "CANCEL";
  file: string;
  app: string;
}

export interface RowPlan {
  tab: string;
  kind: Exclude<ImportTabKind, "schedule">;
  n: number;
  customer: string;
  /** The PO / PT cell, first 120 characters. */
  cell: string;
  outcome: RowOutcome;
  /** Several lines; some numbers gone: beside the outcome (a partial row may split too). */
  split: boolean;
  partial: boolean;
  drop: DropReason | null;
  /**
   * The open pick tickets and SOs it matched, and how: its cell's numbers, its WMS cell, or — a
   * Deposco row naming a PO and no SO — that PO (and DC) on exactly one open Deposco line each.
   */
  refs: string[];
  via: "cell" | "wms" | "po" | "";
  /** Numbers in the cell open nowhere (shipped, cancelled, a typo); POs of its own pick tickets aside. */
  gone: string[];
  /** Open ones with no line yet (no WMS #, waiting for a group). */
  waiting: string[];
  lines: string[];
  source: "A2000" | "Deposco" | "both" | "";
  customerCode: string;
  /** The typed customer is not the source's, spelling aside. */
  customerDiffers: boolean;
  diffs: ImportDiff[];
  /** A range too wide, a cell nobody can read. */
  error: string;
  /**
   * `ambiguous`: the open Deposco lines a PO it names is on, the cell naming no DC to choose by
   * (`13 SO / 50264439` on DC 299 and DC 499). Decided in Step 3 (the next release).
   */
  candidates: string[];
  /**
   * What the row typed, as the line's fields (`typedFields`): a date as its day when it reads as one,
   * else the text as typed. Rows with a line or candidates only.
   */
  typed: Partial<Record<WarehouseField, string>>;
  /** Its routing status as its texts say it (`routingGuess`); null: Not routed. */
  routing: RoutingGuess | null;
  /** The texts the guess came from, for Step 3's table. */
  routingFrom: string;
  /** The year its short dates take (`rowYear`). */
  year: number;
}

export interface ImportProblem {
  tab: string;
  n: number;
  customer: string;
  column: string;
  problem: string;
  value: string;
  /** The dates the text holds (a date column's prose). */
  dates: string[];
}

export interface UnknownValue {
  list: ListName;
  value: string;
  rows: number;
  /** A value of the list it looks like (`looksAlike`), else "". */
  lookAlike: string;
  /** Where it is: "PE PRODUCTION CHECK OFF 561", the first 20. */
  where: string[];
}

export interface ImportConflict {
  ref: string;
  /** The line it is on: the line a row that loses it does not land on. */
  line: string;
  rows: { tab: string; n: number }[];
}

export interface EntryPlan {
  tab: string;
  n: number;
  day: string;
  customer: string;
  co: string;
  pos: string[];
  refs: string[];
  /** The open pick tickets and SOs it reaches (by number, or by PO), and their lines. */
  matched: string[];
  lines: string[];
  carrierText: string;
  /** The carrier it maps to (`carrierFor`), else null: the text goes to the pickup's Notes. */
  carrier: { key: string; code: string; name: string; source: LineSource } | null;
  loadRef: string;
  appTime: string;
  outcome: "import" | "no-match" | "exists";
  /** One shipping row per line: the cartons and pallets of the rows that reach it. */
  shipping: { line: string; ctns: number | null; plts: number | null }[];
  loader: string;
  inTime: string;
  outTime: string;
  trailer: string;
  door: string;
  wmsAsnTrip: string;
  notes: string;
}

export interface NotInFile {
  ref: string;
  source: LineSource;
  wh: string;
  wait: ImportRef["wait"];
  customerName: string;
  customerCode: string;
  po: string;
  line: string;
}

export interface TabPlan {
  name: string;
  kind: ImportTabKind | null;
  hidden: boolean;
  rowCount: number;
  ticked: boolean;
  /** Why it is not offered, or not ticked at first. */
  note: string;
}

export interface ImportPlan {
  /** The file read; `hash` its SHA-256 (the route's; a check's plan has none). */
  file: { name: string; size: number; hash?: string };
  plannedAt: string;
  cutoff: string;
  loadedAt: ImportSource["loadedAt"];
  tabs: TabPlan[];
  rows: RowPlan[];
  conflicts: ImportConflict[];
  problems: ImportProblem[];
  unknown: UnknownValue[];
  entries: EntryPlan[];
  /** Distinct carrier texts the app cannot map, with how many entries have each. */
  carrierTexts: { text: string; entries: number }[];
  notInFile: NotInFile[];
  writes: ImportWrites;
  /** The lines its rows, candidates and schedule entries reach: what Step 3 and the run read of them. */
  lines: ImportLine[];
}

export interface ImportWrites {
  lines: number;
  copies: number;
  pickups: number;
  shippingRows: number;
  listValues: number;
  total: number;
  /** At `SECONDS_PER_WRITE`, `IN_FLIGHT` at a time. */
  seconds: number;
}

/** Measured on staging: a CRUD write takes ~0.4 s; the run keeps 4 in flight. */
export const SECONDS_PER_WRITE = 0.4;
export const IN_FLIGHT = 4;

const limitOf = (field: string) => {
  const f: { name: string; max?: number } | undefined = ROUTING_LINES.fields.find((x) => x.name === field);
  return f?.max;
};

/** The text columns each kind fills, with the line field whose limit applies. */
const TEXT_FIELDS: Record<
  Exclude<ImportTabKind, "schedule">,
  readonly { cell: string; header: string; field: WarehouseField | "notes" }[]
> = {
  production: [
    { cell: "workNeeded", header: "Work Needed", field: "workNeeded" },
    { cell: "productionNotes", header: "Production Notes", field: "productionNotes" },
    { cell: "routingNotes", header: "Routing Notes", field: "routingNotes" },
    { cell: "routingRef", header: "Routing Reference", field: "routingRef" },
    { cell: "inventoryNotes", header: "Inventory Notes", field: "inventoryNotes" },
    { cell: "lead", header: "Lead", field: "lead" },
    { cell: "timing", header: "TIMMING FOR REWORK", field: "timing" },
    { cell: AFTER_INV, header: "Notes (after INV.)", field: "notes" },
  ],
  htsm: [
    { cell: "workNeeded", header: "Work Needed", field: "workNeeded" },
    { cell: "productionNotes", header: "Production Notes", field: "productionNotes" },
    { cell: "routingRef", header: "Routing Conf#", field: "routingRef" },
    { cell: "notes", header: "Notes", field: "notes" },
  ],
  "pe-orders": [
    { cell: "pullGroup", header: "PULL GROUP #", field: "pullGroup" },
    { cell: "locQty", header: "LOC QTY", field: "locQty" },
    { cell: "assignedTo", header: "ASSIGNED TO", field: "peAssignedTo" },
    { cell: "status", header: "STATUS", field: "peStatus" },
    { cell: "comments", header: "COMMENTS", field: "peComments" },
  ],
};

/** The date columns each kind fills: a day, or ASAP where the field takes it. */
const DATE_FIELDS: Record<
  Exclude<ImportTabKind, "schedule">,
  readonly { cell: string; header: string; asap: boolean }[]
> = {
  production: [
    { cell: "ready", header: "READY DATE", asap: true },
    { cell: "schedShip", header: "SCHEDULED SHIP DATE", asap: false },
  ],
  htsm: [{ cell: "schedShip", header: "Scheduled Ship Date", asap: false }],
  "pe-orders": [{ cell: "shipDate", header: "SHIP DATE", asap: true }],
};

/** The list columns: the list, and whether a cell holds several values split on "/". */
const LIST_CELLS: Record<
  Exclude<ImportTabKind, "schedule">,
  readonly { cell: string; field: WarehouseField; split: boolean }[]
> = {
  production: [
    { cell: "workNeeded", field: "workNeeded", split: true },
    { cell: "inventoryNotes", field: "inventoryNotes", split: false },
    { cell: "lead", field: "lead", split: false },
  ],
  htsm: [{ cell: "workNeeded", field: "workNeeded", split: true }],
  "pe-orders": [
    { cell: "status", field: "peStatus", split: false },
    { cell: "assignedTo", field: "peAssignedTo", split: true },
  ],
};

/**
 * A list cell's values: split on "/" where the field holds several — never a date's (`NO TKTS NEEDED
 * 9/28`); a Lead's trailing date cut off.
 */
export function listParts(cell: ImportCell | undefined, field: WarehouseField, split: boolean): string[] {
  const text = cellText(cell);
  if (!text) return [];
  const parts = split ? text.split(/(?<!\d)\/|\/(?!\d)/) : [text];
  return parts
    .map((p) => p.replace(/\s+/g, " ").trim())
    .map((p) => (field === "lead" ? p.replace(/\s+\d{1,2}\/\d{1,2}(\/\d{2,4})?$/, "").trim() : p))
    .map((p) =>
      field === "peAssignedTo" ? p.replace(ESTIMATE, (all, inner: string) => (estimateIn(inner) ? "" : all)).trim() : p,
    )
    .filter(Boolean);
}

const ESTIMATE = /\(([^)]*)\)/g;
/** An estimate in minutes (`45MINS`, `1H 15M`), as `parseEstimate` reads it; "" when it is none. */
const estimateIn = (text: string) => {
  const p = parseEstimate(text);
  return p.ok ? p.value : "";
};
/** The first estimate an Assigned to cell holds in brackets (`MANUEL (45MINS)` → "45"), else "". */
export const estimateOf = (cell: ImportCell | undefined) => {
  for (const m of cellText(cell).matchAll(ESTIMATE)) {
    const v = estimateIn(m[1] ?? "");
    if (v) return v;
  }
  return "";
};
/** A Lead cell's trailing date (`OLIVIA 10/27` → that day), else null. */
export const leadDate = (cell: ImportCell | undefined, year: number) => {
  const m = /\s(\d{1,2}\/\d{1,2}(?:\/\d{2,4})?)$/.exec(cellText(cell));
  return m?.[1] ? cellDay(m[1], year) : null;
};

/**
 * A carrier text of the schedule mapped to a carrier of `routing-carriers`: a SCAC or code it names
 * (`TForce Freight / TFIN` → TFIN, `Estes Express (EXLA)`), else a name that looks alike. Null when
 * none or several do: the text then goes to the pickup's Notes. Never a typed carrier.
 */
export function carrierFor(text: string, options: readonly CarrierOption[]): CarrierOption | null {
  const t = text.replace(/\s+/g, " ").trim();
  if (!t) return null;
  const words = new Set(
    t
      .toUpperCase()
      .split(/[^A-Z0-9]+/)
      .filter((w) => w.length >= 3),
  );
  const byCode = options.filter(
    (o) => (o.scac && words.has(o.scac.toUpperCase())) || o.codes.some((c) => words.has(c.toUpperCase())),
  );
  if (byCode.length === 1) return byCode[0]!;
  const name = t
    .replace(/\(.*?\)/g, " ")
    .split("/")[0]!
    .trim();
  const byName = options.filter((o) => looksAlike(o.name, name));
  if (byName.length === 1) return byName[0]!;
  const exact = byName.filter((o) => customerSpelling(o.name) === customerSpelling(name));
  return exact.length === 1 ? exact[0]! : null;
}

/** The fields a row's kind fills: the check-off and HT & SM a line's, PE ORDERS its PE Order columns. */
export const fieldsOf = (k: RowPlan["kind"]): "line" | "pe" => (k === "pe-orders" ? "pe" : "line");

const dayOrText = (c: ImportCell | undefined, year: number, asap = false) => {
  const text = cellText(c instanceof Date && isTimeOnly(c) ? c.toISOString().slice(11, 19) : c);
  if (asap && /^asap$/i.test(text)) return PE_ASAP;
  return cellDay(c, year) ?? text;
};
/** A date and time cell as a PE Order moment ("2026-10-30 14:05"); "" for none or a time alone. */
const momentOfCell = (c: ImportCell | undefined) =>
  c instanceof Date
    ? isTimeOnly(c)
      ? ""
      : `${dayOf(c)} ${pad(c.getUTCHours())}:${pad(c.getUTCMinutes())}`
    : cellText(c);

/**
 * A row's typed values as the line's fields, before any decision (Step 3 decides prose dates, list
 * values, text over a limit): a date as its day where it reads as one, else as typed.
 */
export function typedFields(
  row: ImportRow,
  kind: RowPlan["kind"],
  year: number,
): Partial<Record<WarehouseField, string>> {
  const c = row.cells;
  const t = (k: string) => cellText(c[k]);
  const out: Partial<Record<WarehouseField, string>> =
    kind === "production"
      ? {
          readyDate: dayOrText(c.ready, year, true),
          workNeeded: t("workNeeded"),
          productionNotes: t("productionNotes"),
          routingNotes: t("routingNotes"),
          routingRef: t("routingRef"),
          schedShip: dayOrText(c.schedShip, year),
          inventoryNotes: t("inventoryNotes"),
          lead: t("lead"),
          timing: t("timing"),
          notes: t(AFTER_INV),
        }
      : kind === "htsm"
        ? {
            workNeeded: t("workNeeded"),
            productionNotes: [t("productionNotes"), c.cartonLabels === true ? "Carton labels sent" : ""]
              .filter(Boolean)
              .join(" · "),
            routingNotes: t("routingStatus"),
            routingRef: t("routingRef"),
            schedShip: dayOrText(c.schedShip, year),
            notes: t("notes"),
          }
        : {
            peShipDate: dayOrText(c.shipDate, year, true),
            pullGroup: t("pullGroup"),
            locQty: t("locQty"),
            peAssignedTo: t("assignedTo"),
            peStatus: t("status"),
            peStartAt: momentOfCell(c.startTime),
            peEndAt: momentOfCell(c.endTime),
            pallets: cellNumber(c.pallets) === null ? "" : String(cellNumber(c.pallets)),
            peComments: t("comments"),
          };
  return Object.fromEntries(Object.entries(out).filter(([, v]) => v));
}

/**
 * A row's routing status (Step 3's preselection): its Routing Notes (HT & SM: Routing Status) saying
 * ROUTED and a day → Routed on it, PRE ROUTED → Pre-routed (`routingFromText`); else a Routing
 * Reference (Routing Conf#) → Routed, no day; else none (Not routed). PE ORDERS has none.
 */
export function routingGuess(
  row: ImportRow,
  kind: RowPlan["kind"],
  year: number,
): Pick<RowPlan, "routing" | "routingFrom"> {
  if (kind === "pe-orders") return { routing: null, routingFrom: "" };
  const notes = kind === "htsm" ? row.cells.routingStatus : row.cells.routingNotes;
  const ref = cellText(row.cells.routingRef);
  const from = [cellText(notes), ref].filter(Boolean).join(" · ");
  const said = routingFromText(notes, year);
  return { routing: said ?? (ref ? { status: "Routed", on: null } : null), routingFrom: from };
}

const firstChars = (s: string, n = 120) => (s.length > n ? `${s.slice(0, n - 1)}…` : s);
const poKey = (po: string) => po.replace(/\s+/g, "").replace(/^0+/, "").toUpperCase();

export interface PlanInput {
  tabs: readonly ParsedTab[];
  /** The tabs ticked (by name); null: `tickedAtFirst`. */
  ticked: readonly string[] | null;
  /** The schedule's first day (YYYY-MM-DD): entries under day sections on or after it. */
  cutoff: string;
  /** Today (YYYY-MM-DD): a short date's year when the row has none. */
  today: string;
  source: ImportSource;
  file: { name: string; size: number; hash?: string };
  plannedAt: string;
}

/** The plan: every row's outcome, and what Step 2 counts. Nothing is written. */
export function planImport(input: PlanInput): ImportPlan {
  const { source } = input;
  const year = Number(input.today.slice(0, 4));
  const tabs: TabPlan[] = input.tabs.map((t) => {
    const ticked = input.ticked ? !!t.kind && !isShippedTab(t) && input.ticked.includes(t.name) : tickedAtFirst(t);
    const note = !t.kind
      ? "Not imported"
      : isShippedTab(t)
        ? SHIPPED_NOTE
        : t.hidden
          ? "Hidden in the file"
          : /^\s*rs\b/i.test(t.name)
            ? "RS warehouse"
            : "";
    return { name: t.name, kind: t.kind, hidden: t.hidden, rowCount: t.rowCount, ticked, note };
  });
  const ticked = input.tabs.filter((t, i) => tabs[i]!.ticked);
  const refOf = new Map(source.refs.map((r) => [r.ref, r]));
  const lineOf = new Map(source.lines.map((l) => [l.key, l]));
  const linesByWms = new Map<string, ImportLine[]>();
  for (const l of source.lines) if (l.wms) linesByWms.set(l.wms, [...(linesByWms.get(l.wms) ?? []), l]);

  // Every row with a PO / PT cell, matched by its cell.
  interface Draft {
    tab: ParsedTab;
    kind: RowPlan["kind"];
    row: ImportRow;
    cell: PoPtCell;
    open: ImportRef[];
    gone: string[];
    via: RowPlan["via"];
    candidates: string[];
  }
  const drafts: Draft[] = [];
  for (const tab of ticked) {
    if (!tab.kind || tab.kind === "schedule") continue;
    for (const row of tab.rows) {
      if (!cellText(row.cells.poPt)) continue;
      const cell = parsePoPt(row.cells.poPt);
      // A 7-digit number that is the PO of an SO the cell names is that PO, open pick ticket or not
      // (`SO233663 / 1808271`: CGES PO 001808271, and an open Macy's pick ticket).
      const sos = cell.sos.map((n) => refOf.get(n)).filter((r): r is ImportRef => !!r);
      const soPos = new Set(sos.map((r) => poKey(r.po)));
      const pts = cell.pts.filter((n) => !soPos.has(poKey(n)));
      const open = [...pts.map((n) => refOf.get(n)).filter((r): r is ImportRef => !!r), ...sos];
      const pos = new Set(open.map((r) => poKey(r.po)));
      const gone = [...pts, ...cell.sos].filter((n) => !refOf.has(n) && !pos.has(poKey(n)));
      drafts.push({ tab, kind: tab.kind, row, cell, open, gone, via: open.length ? "cell" : "", candidates: [] });
    }
  }
  // The WMS cell, only for a row naming no open one, and only when no row names that WMS's.
  const named = new Set(drafts.flatMap((d) => d.open.map((r) => r.ref)));
  for (const d of drafts) {
    if (d.open.length) continue;
    const refs = wmsNumbers(d.row.cells.wms)
      .flatMap((w) => linesByWms.get(w) ?? [])
      .flatMap((l) => l.refs);
    if (refs.length && refs.every((r) => !named.has(r))) {
      d.open = refs.map((r) => refOf.get(r)!).filter(Boolean);
      d.via = "wms";
    }
  }
  // A Deposco row naming a PO and no SO (`13 SO / 50264439 DC# 699`): the Deposco line key is customer
  // + PO (+ DC), so each PO it names is the open Deposco line of that PO — of the DC it names, when it
  // names one. A PO on several lines with no DC to choose by is ambiguous. A2000 rows match pick tickets only.
  const refsByPo = new Map<string, ImportRef[]>();
  for (const r of source.refs) if (r.po) refsByPo.set(poKey(r.po), [...(refsByPo.get(poKey(r.po)) ?? []), r]);
  for (const d of drafts) {
    if (d.open.length || d.cell.pts.length || d.cell.sos.length) continue;
    const text = cellText(d.row.cells.poPt);
    const dc = /\bDC\s*#?\s*([A-Z0-9]+)/i.exec(text)?.[1]?.replace(/^0+/, "").toUpperCase() ?? "";
    const perPo = [...new Set([...text.matchAll(/(?<!\d)\d{5,}(?!\d)/g)].map((m) => poKey(m[0])))]
      .map((po) => [
        ...new Set(
          (refsByPo.get(po) ?? [])
            .filter((r) => r.source === "Deposco" && r.line)
            .map((r) => r.line)
            .filter((k) => !dc || (k.split("|")[3] ?? "").replace(/^0+/, "").toUpperCase() === dc),
        ),
      ])
      .filter((keys) => keys.length);
    if (perPo.some((keys) => keys.length > 1)) d.candidates = [...new Set(perPo.flat())];
    else if (perPo.length) {
      d.open = perPo.flat().flatMap((k) => (lineOf.get(k)?.refs ?? []).map((r) => refOf.get(r)!).filter(Boolean));
      d.via = "po";
    }
  }
  // A pick ticket or SO named in two live rows of the same fields: the check-off and HT & SM fill a
  // line's fields, PE ORDERS its PE Order columns — a PE row and its PE ORDERS row are one line.
  // Rows landing on the same lines are not a conflict: they are one line's rows (`lineGroups`).
  const namers = new Map<string, Draft[]>();
  for (const d of drafts)
    if (d.via === "cell" || d.via === "po")
      for (const r of d.open) {
        const key = `${fieldsOf(d.kind)}|${r.ref}`;
        namers.set(key, [...(namers.get(key) ?? []), d]);
      }
  const landsOn = (d: Draft) => [...new Set(d.open.map((r) => r.line).filter(Boolean))].sort().join(",");
  const conflicts: ImportConflict[] = [];
  const inConflict = new Set<Draft>();
  for (const [key, ds] of namers) {
    if (ds.length < 2 || new Set(ds.map(landsOn)).size < 2) continue;
    const ref = key.slice(key.indexOf("|") + 1);
    conflicts.push({ ref, line: refOf.get(ref)?.line ?? "", rows: ds.map((d) => ({ tab: d.tab.name, n: d.row.n })) });
    ds.forEach((d) => inConflict.add(d));
  }

  const rows: RowPlan[] = drafts.map((d) => {
    const lineRefs = d.open.filter((r) => r.line);
    const waiting = d.open.filter((r) => !r.line).map((r) => r.ref);
    const lines = [...new Set(lineRefs.map((r) => r.line))];
    const sources = new Set(d.open.map((r) => r.source));
    const year0 = rowYear(d.row, year);
    const typed = cellText(d.row.cells.customer);
    const first = d.open[0];
    const differs =
      !!first &&
      !!typed &&
      ![first.customerName, first.customerCode].some((n) => {
        const a = customerSpelling(n);
        const b = customerSpelling(typed);
        return !!a && !!b && (a.includes(b) || b.includes(a));
      });
    const error = !d.open.length && d.cell.tooWide.length ? `Range too wide: ${d.cell.tooWide.join(", ")}` : "";
    const named = d.cell.pts.length + d.cell.sos.length > 0;
    const drop: DropReason | null =
      lines.length || d.candidates.length
        ? null
        : waiting.length
          ? d.open.some((r) => r.wait === "no-wms")
            ? "no-wms"
            : "group"
          : named
            ? "closed"
            : "no-number";
    const exists = lines.some((k) => lineOf.get(k)?.stored);
    const outcome: RowOutcome = inConflict.has(d)
      ? "conflict"
      : lines.length
        ? exists
          ? "exists"
          : d.gone.length
            ? "partial"
            : lines.length > 1
              ? "import-split"
              : "import"
        : d.candidates.length
          ? "ambiguous"
          : error
            ? "error"
            : "dropped";
    return {
      tab: d.tab.name,
      year: year0,
      kind: d.kind,
      n: d.row.n,
      customer: typed,
      cell: firstChars(cellText(d.row.cells.poPt)),
      outcome,
      split: lines.length > 1,
      partial: !!lines.length && d.gone.length > 0,
      drop,
      refs: d.open.map((r) => r.ref),
      via: d.via,
      gone: d.gone,
      waiting,
      lines,
      source: sources.size === 2 ? "both" : sources.has("Deposco") ? "Deposco" : sources.size ? "A2000" : "",
      customerCode: first?.customerCode ?? "",
      customerDiffers: differs,
      diffs: lines.length ? diffsOf(d.row, lineRefs, year0) : [],
      error: d.cell.tooWide.length && !error ? `Range too wide: ${d.cell.tooWide.join(", ")}` : error,
      candidates: d.candidates,
      ...(lines.length || d.candidates.length
        ? { typed: typedFields(d.row, d.kind, year0), ...routingGuess(d.row, d.kind, year0) }
        : { typed: {}, routing: null, routingFrom: "" }),
    };
  });

  const live = (r: RowPlan) => r.lines.length > 0;
  const liveDrafts = drafts.filter((_, i) => live(rows[i]!));

  // Text and dates the fields can't hold, and list values the lists don't have: live rows only.
  const problems: ImportProblem[] = [];
  const unknownMap = new Map<string, UnknownValue>();
  const byList = (list: ListName) => source.listValues.filter((v) => v.list === list);
  for (const d of liveDrafts) {
    const y = rowYear(d.row, year);
    const base = { tab: d.tab.name, n: d.row.n, customer: cellText(d.row.cells.customer) };
    for (const f of DATE_FIELDS[d.kind]) {
      const c = d.row.cells[f.cell];
      if (c === undefined || c === null) continue;
      const text = cellText(c instanceof Date && isTimeOnly(c) ? c.toISOString().slice(11, 19) : c);
      if (cellDay(c, y) || (f.asap && /^asap$/i.test(text))) continue;
      problems.push({ ...base, column: f.header, problem: "Not a date", value: text, dates: datesInText(text, y) });
    }
    for (const f of TEXT_FIELDS[d.kind]) {
      const text = cellText(d.row.cells[f.cell]);
      const max = limitOf(f.field);
      if (max && text.length > max)
        problems.push({
          ...base,
          column: f.header,
          problem: `${text.length} characters > ${max}`,
          value: text,
          dates: [],
        });
    }
    for (const f of LIST_CELLS[d.kind]) {
      const list = FIELD_LIST[f.field];
      if (!list) continue;
      const values = byList(list);
      for (const part of listParts(d.row.cells[f.cell], f.field, f.split)) {
        if (sameListValue(part, values)) continue;
        const key = `${list}|${part.toUpperCase()}`;
        const u = unknownMap.get(key) ?? {
          list,
          value: part,
          rows: 0,
          lookAlike: values.find((v) => v.retired !== "Yes" && looksAlike(v.value, part))?.value ?? "",
          where: [],
        };
        u.rows++;
        if (u.where.length < 20) u.where.push(`${d.tab.name.trim()} ${d.row.n}`);
        unknownMap.set(key, u);
      }
    }
  }

  // The Shipping Schedule's entries from the cut-off.
  const scheduleTabs = ticked.filter((t) => t.kind === "schedule");
  const entries = scheduleTabs.flatMap((t) => scheduleEntries(t, input.cutoff, year));
  const carrierCounts = new Map<string, number>();
  const entryPlans: EntryPlan[] = entries.map((e) => {
    const byNumber = e.refs.map((n) => refOf.get(n)).filter((r): r is ImportRef => !!r);
    const byPo = e.posWithoutRef.flatMap((po) => refsByPo.get(poKey(po)) ?? []);
    const matched = [...new Map([...byNumber, ...byPo].map((r) => [r.ref, r])).values()];
    const lines = [...new Set(matched.map((r) => r.line).filter(Boolean))];
    const carrier = carrierFor(e.carrierText, source.carriers);
    if (e.carrierText && !carrier) carrierCounts.set(e.carrierText, (carrierCounts.get(e.carrierText) ?? 0) + 1);
    const pickupIds = new Set(
      source.pickups
        .filter((p) => p.status !== "Cancelled" && p.pickupDate === e.day && poKey(p.loadRef) === poKey(e.loadRef))
        .map((p) => p.id),
    );
    const lineIds = new Set(lines.map((k) => lineOf.get(k)?.id).filter(Boolean));
    const exists = source.shippingRows.some(
      (s) => pickupIds.has(s.pickupId) && s.status !== "Removed" && lineIds.has(s.lineId),
    );
    return {
      tab: e.tab,
      n: e.n,
      day: e.day,
      customer: e.customer,
      co: e.co,
      pos: e.pos,
      refs: e.refs,
      matched: matched.map((r) => r.ref),
      lines,
      carrierText: e.carrierText,
      carrier: carrier ? { key: carrier.key, code: carrier.code, name: carrier.name, source: carrier.source } : null,
      loadRef: e.loadRef,
      appTime: e.appTime,
      outcome: !lines.length ? "no-match" : exists ? "exists" : "import",
      shipping: shippingOf(
        e.parts,
        lines,
        (n) => refOf.get(n),
        (po) => refsByPo.get(poKey(po)) ?? [],
      ),
      loader: e.loader,
      inTime: e.inTime,
      outTime: e.outTime,
      trailer: e.trailer,
      door: e.door,
      wmsAsnTrip: e.wmsAsnTrip,
      notes: e.notes,
    };
  });

  // Open in the app, in no row of the file (the schedule aside).
  const covered = new Set(rows.flatMap((r) => [...r.refs, ...r.waiting]));
  const notInFile: NotInFile[] = source.refs
    .filter((r) => !covered.has(r.ref))
    .map((r) => ({
      ref: r.ref,
      source: r.source,
      wh: r.wh,
      wait: r.wait,
      customerName: r.customerName,
      customerCode: r.customerCode,
      po: r.po,
      line: r.line ? (lineOf.get(r.line)?.place ?? r.line) : "",
    }));

  // The writes a run makes with every default: new lines and their copies, pickups and their rows,
  // and each unknown list value added (the most it can be).
  const newLines = new Set(
    rows.filter((r) => r.outcome !== "conflict").flatMap((r) => r.lines.filter((k) => !lineOf.get(k)?.stored)),
  );
  const importing = entryPlans.filter((e) => e.outcome === "import");
  const writes = {
    lines: newLines.size,
    copies: [...newLines].reduce((n, k) => n + (lineOf.get(k)?.copies ?? 0), 0),
    pickups: importing.length,
    shippingRows: importing.reduce((n, e) => n + e.lines.length, 0),
    listValues: unknownMap.size,
  };
  const total = writes.lines + writes.copies + writes.pickups + writes.shippingRows + writes.listValues;

  return {
    file: input.file,
    plannedAt: input.plannedAt,
    cutoff: input.cutoff,
    loadedAt: source.loadedAt,
    tabs,
    rows,
    conflicts,
    problems,
    unknown: [...unknownMap.values()].sort((a, b) => a.list.localeCompare(b.list) || b.rows - a.rows),
    entries: entryPlans,
    carrierTexts: [...carrierCounts].map(([text, n]) => ({ text, entries: n })).sort((a, b) => b.entries - a.entries),
    notInFile,
    writes: { ...writes, total, seconds: Math.round((total * SECONDS_PER_WRITE) / IN_FLIGHT) },
    lines: [...new Set([...rows.flatMap((r) => [...r.lines, ...r.candidates]), ...entryPlans.flatMap((e) => e.lines)])]
      .map((k) => lineOf.get(k))
      .filter((l): l is ImportLine => !!l),
  };
}

/** QTY, FIRST SHIP and CANCEL as the row says them against its open pick tickets' and SOs'. */
function diffsOf(row: ImportRow, refs: readonly ImportRef[], year: number): ImportDiff[] {
  const out: ImportDiff[] = [];
  const qty = cellNumber(row.cells.qty);
  const appQty = refs.reduce((n, r) => n + (r.qty ?? 0), 0);
  if (qty !== null && refs.length && qty !== appQty) out.push({ field: "QTY", file: String(qty), app: String(appQty) });
  const earliest = (days: (string | null)[]) => days.filter((d): d is string => !!d).sort()[0] ?? "";
  for (const [field, cell, app] of [
    ["FIRST SHIP", row.cells.start, earliest(refs.map((r) => r.start))],
    ["CANCEL", row.cells.cancel, earliest(refs.map((r) => r.cancel))],
  ] as const) {
    const day = cellDay(cell, year);
    if (day && app && day !== app) out.push({ field, file: day, app });
  }
  return out;
}

interface ScheduleEntry {
  tab: string;
  n: number;
  day: string;
  customer: string;
  co: string;
  pos: string[];
  /** The POs of its rows that name no pick ticket or SO: matched by PO. */
  posWithoutRef: string[];
  refs: string[];
  carrierText: string;
  loadRef: string;
  appTime: string;
  /** Its rows' own numbers, cartons and pallets (`CTNS`, `PLTS` are per row: per pick ticket or PO). */
  parts: EntryPart[];
  loader: string;
  inTime: string;
  outTime: string;
  trailer: string;
  door: string;
  wmsAsnTrip: string;
  notes: string;
}

export interface EntryPart {
  refs: string[];
  po: string;
  ctns: number | null;
  plts: number | null;
}

/**
 * An entry's shipping rows: one per line it reaches, with the cartons and pallets of its rows that
 * reach that line (a row reaching none counts on the entry's first line). Empty when none says any.
 */
function shippingOf(
  parts: readonly EntryPart[],
  lines: readonly string[],
  byRef: (n: string) => ImportRef | undefined,
  byPo: (po: string) => readonly ImportRef[],
): EntryPlan["shipping"] {
  const sum = new Map(lines.map((l) => [l, { ctns: null as number | null, plts: null as number | null }]));
  for (const p of parts) {
    const reached = [
      ...new Set(
        (p.refs.length ? p.refs.map(byRef).filter((r): r is ImportRef => !!r) : p.po ? byPo(p.po) : [])
          .map((r) => r.line)
          .filter((l) => sum.has(l)),
      ),
    ];
    const at = sum.get(reached[0] ?? lines[0] ?? "");
    if (!at) continue;
    if (p.ctns !== null) at.ctns = (at.ctns ?? 0) + p.ctns;
    if (p.plts !== null) at.plts = (at.plts ?? 0) + p.plts;
  }
  return [...sum].map(([line, v]) => ({ line, ...v }));
}

/** The schedule's cut-off: the first year a cut-off may name (a year typed halfway is 0001, 0020…). */
export const CUTOFF_FROM_YEAR = 2020;
/** Why a cut-off can't be planned with, or null: a complete date (YYYY-MM-DD), a real day, from 2020. */
export function cutoffProblem(v: string): string | null {
  const whole = "Enter a whole date: month, day and year.";
  const m = /^(\d{4})-\d{2}-\d{2}$/.exec(v);
  if (!m) return whole;
  // The year first: Date reads a year under 100 as 19xx.
  if (Number(m[1]) < CUTOFF_FROM_YEAR) return `The year is ${m[1]}: the cut-off is from ${CUTOFF_FROM_YEAR} on.`;
  return cellDay(v, 0) === v ? null : whole;
}

/** A time cell as H:MM (`8:00`); text as it is. */
export function cellTime(c: ImportCell | undefined): string {
  if (c instanceof Date) return `${c.getUTCHours()}:${pad(c.getUTCMinutes())}`;
  if (typeof c === "number" && c >= 0 && c < 1) {
    const minutes = Math.round(c * 24 * 60);
    return `${Math.floor(minutes / 60)}:${pad(minutes % 60)}`;
  }
  return cellText(c);
}

/**
 * The schedule's pickup entries on or after `cutoff`. A row with a date and nothing else is a day
 * section; a row with a date and a customer starts an entry (its day is the section's, whatever its
 * own date cell says); the rows under it with only PO# / PT # cells (a customer at most: another DC)
 * belong to it.
 */
export function scheduleEntries(tab: ParsedTab, cutoff: string, year: number): ScheduleEntry[] {
  const out: ScheduleEntry[] = [];
  let day = "";
  let current: ScheduleEntry | null = null;
  for (const row of tab.rows) {
    const c = row.cells;
    const date = c.date;
    const hasDate = date !== undefined && date !== null;
    const po = cellText(c.po);
    const ref = c.ref;
    const others = Object.keys(c).filter((k) => k !== "date");
    if (hasDate && !others.length) {
      day = cellDay(date, year) ?? datesInText(cellText(date), year)[0] ?? day;
      current = null;
      continue;
    }
    const cell = parsePoPt(ref);
    const refs = [...cell.pts, ...cell.sos];
    if (hasDate && (cellText(c.customer) || po || refs.length)) {
      current = null;
      if (!day || day < cutoff) continue;
      current = {
        tab: tab.name,
        n: row.n,
        day,
        customer: cellText(c.customer),
        co: cellText(c.co),
        pos: po ? [po] : [],
        posWithoutRef: po && !refs.length ? [po] : [],
        refs,
        carrierText: cellText(c.carrier),
        loadRef: cellText(c.loadRef),
        appTime: cellTime(c.appTime),
        parts: [{ refs, po, ctns: cellNumber(c.ctns), plts: cellNumber(c.plts) }],
        loader: cellText(c.loader),
        inTime: cellTime(c.inTime),
        outTime: cellTime(c.outTime),
        trailer: cellText(c.trailer),
        door: cellText(c.door),
        wmsAsnTrip: cellText(c.wmsAsnTrip),
        notes: cellText(c.notes),
      };
      out.push(current);
      continue;
    }
    if (!current || hasDate || (!po && !refs.length)) continue;
    if (po) current.pos.push(po);
    if (po && !refs.length) current.posWithoutRef.push(po);
    for (const r of refs) if (!current.refs.includes(r)) current.refs.push(r);
    current.parts.push({ refs, po, ctns: cellNumber(c.ctns), plts: cellNumber(c.plts) });
  }
  return out;
}

// ── Several rows on one line ─────────────────────────────────────────────────────────────────

/** A row's id across the wizard: its tab and Excel row. */
export const rowId = (r: Pick<RowPlan, "tab" | "n">) => `${r.tab}|${r.n}`;

/**
 * Rows landing on one line and filling the same fields (the check-off and HT & SM a line's, PE ORDERS
 * its PE Order columns) — one PO split over rows (`6 SO / 0008896157` on 12 rows). Not people
 * disagreeing: rows whose typed entries are equal merge into one, silently (`equal`); rows that
 * differ are one decision per line in Step 3. A row is on the lines `linesOf` gives it: by default its
 * own, none while it is a conflict or ambiguous (Step 3 places it).
 */
export interface LineGroup {
  /** `<fields>|<line key>`. */
  id: string;
  line: string;
  fields: "line" | "pe";
  rows: string[];
  equal: boolean;
}

const sameEntries = (rows: readonly RowPlan[]) =>
  new Set(rows.map((r) => JSON.stringify([Object.entries(r.typed).sort(), r.routing]))).size === 1;

export function lineGroups(
  rows: readonly RowPlan[],
  linesOf: (r: RowPlan) => readonly string[] = (r) =>
    r.outcome === "conflict" || r.outcome === "ambiguous" ? [] : r.lines,
): LineGroup[] {
  const by = new Map<string, RowPlan[]>();
  for (const r of rows)
    for (const line of linesOf(r)) {
      const id = `${fieldsOf(r.kind)}|${line}`;
      by.set(id, [...(by.get(id) ?? []), r]);
    }
  return [...by]
    .filter(([, rs]) => rs.length > 1)
    .map(([id, rs]) => ({
      id,
      line: id.slice(id.indexOf("|") + 1),
      fields: id.startsWith("pe|") ? ("pe" as const) : ("line" as const),
      rows: rs.map(rowId),
      equal: sameEntries(rs),
    }));
}

// ── Step 2: what was found ───────────────────────────────────────────────────────────────────

export const IMPORT_CARDS = [
  "tabs",
  "open",
  "dropped",
  "attention",
  "differences",
  "problems",
  "unknown",
  "not-in-file",
  "schedule",
] as const;
export type ImportCardId = (typeof IMPORT_CARDS)[number];

/** A card's list: its columns and rows, as the page shows it and the download writes it. */
export interface ImportList {
  title: string;
  columns: { key: string; label: string }[];
  rows: Record<string, string | number>[];
}

export interface ImportCard {
  id: ImportCardId;
  title: string;
  count: number;
  /** Its counts in words, one line each. */
  lines: string[];
  list: ImportList;
}

const n = (v: number) => v.toLocaleString("en-US");
const countBy = <T>(xs: readonly T[], key: (x: T) => string) => {
  const m = new Map<string, number>();
  for (const x of xs) m.set(key(x), (m.get(key(x)) ?? 0) + 1);
  return m;
};
const ROW_COLUMNS = [
  { key: "tab", label: "Tab" },
  { key: "n", label: "Excel row" },
  { key: "customer", label: "Customer (file)" },
  { key: "cell", label: "PO / PT (file)" },
];
const rowOf = (r: RowPlan) => ({ tab: r.tab.trim(), n: r.n, customer: r.customer, cell: r.cell });
const list = (x: readonly string[]) => x.join(", ");

/** "6 min 30 s". */
export const durationText = (seconds: number) =>
  seconds < 60 ? `${seconds} s` : `${Math.floor(seconds / 60)} min${seconds % 60 ? ` ${seconds % 60} s` : ""}`;

/** Step 2's cards, in order, each with its count, its lines and its list. */
export function importCards(plan: ImportPlan): ImportCard[] {
  const live = plan.rows.filter((r) => r.lines.length > 0);
  const dropped = plan.rows.filter((r) => !r.lines.length && r.outcome !== "ambiguous");
  const ambiguous = plan.rows.filter((r) => r.outcome === "ambiguous");
  const byTab = (rows: readonly RowPlan[]) => [...countBy(rows, (r) => r.tab.trim())].map(([t, c]) => `${t}: ${n(c)}`);
  const lineKeys = new Set(live.filter((r) => r.outcome !== "conflict").flatMap((r) => r.lines));
  const groups = lineGroups(plan.rows);
  const merged = groups.filter((g) => g.equal);
  const toDecide = groups.filter((g) => !g.equal);
  const inGroups = new Map<string, LineGroup>();
  for (const g of groups) for (const id of g.rows) inGroups.set(id, g);
  const attention = plan.rows.filter(
    (r) =>
      r.outcome === "conflict" ||
      r.outcome === "ambiguous" ||
      r.partial ||
      r.split ||
      r.outcome === "exists" ||
      inGroups.has(rowId(r)),
  );
  const differs = live.filter((r) => r.diffs.length);
  const notIn = plan.notInFile;
  const deposcoNotIn = notIn.filter((r) => r.source === "Deposco");
  const topDeposco = [...countBy(deposcoNotIn, (r) => r.customerName || r.customerCode)]
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5)
    .map(([c, k]) => `${c} ${n(k)}`);
  const entries = plan.entries;
  const w = plan.writes;
  return [
    {
      id: "tabs",
      title: "Tabs found",
      count: plan.tabs.filter((t) => t.kind).length,
      lines: [
        `${n(plan.tabs.filter((t) => t.ticked).length)} ticked of ${n(plan.tabs.length)} tabs`,
        `Not imported: ${list(plan.tabs.filter((t) => !t.kind || isShippedTab(t)).map((t) => t.name.trim()))}`,
        ...plan.tabs.filter((t) => t.kind && isShippedTab(t)).map((t) => `${t.name.trim()}: ${SHIPPED_NOTE}`),
      ],
      list: {
        title: "Tabs",
        columns: [
          { key: "name", label: "Tab" },
          { key: "kind", label: "Kind" },
          { key: "rows", label: "Rows" },
          { key: "ticked", label: "Imported" },
          { key: "note", label: "Note" },
        ],
        rows: plan.tabs.map((t) => ({
          name: t.name.trim(),
          kind: t.kind ? IMPORT_KIND_LABELS[t.kind] : "",
          rows: t.rowCount,
          ticked: t.ticked ? "Yes" : "No",
          note: t.note,
        })),
      },
    },
    {
      id: "open",
      title: "Rows still open in the app",
      count: live.length,
      lines: [
        ...byTab(live),
        `A2000 ${n(live.filter((r) => r.source === "A2000").length)} · Deposco ${n(live.filter((r) => r.source === "Deposco").length)} · both ${n(live.filter((r) => r.source === "both").length)}`,
        `${n(lineKeys.size)} lines (${n(live.filter((r) => r.via === "wms").length)} rows by their WMS cell, ${n(live.filter((r) => r.via === "po").length)} Deposco rows by their PO and DC)`,
        `${n(w.total)} writes: ${n(w.lines)} lines, ${n(w.copies)} pick-ticket copies, ${n(w.pickups)} pickups, ${n(w.shippingRows)} shipping rows, up to ${n(w.listValues)} list values — about ${durationText(w.seconds)}`,
      ],
      list: {
        title: "Rows still open",
        columns: [
          ...ROW_COLUMNS,
          { key: "source", label: "Source" },
          { key: "refs", label: "Open PT / SO" },
          { key: "via", label: "Matched by" },
          { key: "lines", label: "Lines" },
          { key: "outcome", label: "Outcome" },
          { key: "customerCode", label: "Customer (source)" },
          { key: "differs", label: "Customer differs" },
        ],
        rows: live.map((r) => ({
          ...rowOf(r),
          source: r.source,
          refs: list(r.refs),
          via: r.via === "wms" ? "WMS cell" : r.via === "po" ? "PO (+ DC)" : "PO / PT cell",
          lines: list(r.lines.map((k) => placeOf(k))),
          outcome: OUTCOME_LABELS[r.outcome],
          customerCode: r.customerCode,
          differs: r.customerDiffers ? "Yes" : "",
        })),
      },
    },
    {
      id: "dropped",
      title: "Rows dropped",
      count: dropped.length,
      lines: [
        ...byTab(dropped),
        ...[...countBy(dropped, (r) => (r.outcome === "error" ? "Can't read" : DROP_LABELS[r.drop ?? "closed"]))].map(
          ([why, c]) => `${why}: ${n(c)}`,
        ),
        "History already holds the Shipped Orders tab",
      ],
      list: {
        title: "Rows dropped",
        columns: [...ROW_COLUMNS, { key: "why", label: "Why" }, { key: "waiting", label: "Open, no line yet" }],
        rows: dropped.map((r) => ({
          ...rowOf(r),
          why: r.outcome === "error" ? r.error : DROP_LABELS[r.drop ?? "closed"],
          waiting: list(r.waiting),
        })),
      },
    },
    {
      id: "attention",
      title: "Conflicts, ambiguous POs, several rows on a line, partial rows, splits, lines already saved",
      count: attention.length,
      lines: [
        `Conflicts: ${n(plan.conflicts.length)} pick tickets / SOs in ${n(plan.rows.filter((r) => r.outcome === "conflict").length)} rows`,
        `PO on several open Deposco lines, no DC in the cell: ${n(ambiguous.length)} rows (${n(new Set(ambiguous.flatMap((r) => r.candidates)).size)} candidate lines)`,
        `Partial (some numbers gone): ${n(live.filter((r) => r.partial).length)}`,
        `Split over several lines: ${n(live.filter((r) => r.split).length)}`,
        `Line already saved: ${n(live.filter((r) => r.outcome === "exists").length)}`,
        `Several rows on one line: ${n(groups.length)} lines — ${n(merged.length)} merged (the same entries, ${n(merged.reduce((k, g) => k + g.rows.length, 0))} rows), ${n(toDecide.length)} to decide in Step 3 (${n(toDecide.reduce((k, g) => k + g.rows.length, 0))} rows)`,
      ],
      list: {
        title: "Needs a decision",
        columns: [
          ...ROW_COLUMNS,
          { key: "what", label: "What" },
          { key: "with", label: "Named also in" },
          { key: "gone", label: "Gone" },
          { key: "lines", label: "Lines" },
          { key: "candidates", label: "Candidate lines" },
        ],
        rows: attention.map((r) => ({
          ...rowOf(r),
          what: [
            r.outcome === "conflict" ? "Conflict" : "",
            r.outcome === "ambiguous" ? OUTCOME_LABELS.ambiguous : "",
            inGroups.has(rowId(r))
              ? inGroups.get(rowId(r))!.equal
                ? "Merged with its line's rows"
                : "Several rows on its line"
              : "",
            r.outcome === "exists" ? "Line already saved" : "",
            r.partial ? "Partial" : "",
            r.split ? "Split" : "",
          ]
            .filter(Boolean)
            .join(", "),
          with: list(
            plan.conflicts
              .filter((c) => c.rows.some((x) => x.tab === r.tab && x.n === r.n))
              .flatMap((c) =>
                c.rows.filter((x) => x.tab !== r.tab || x.n !== r.n).map((x) => `${x.tab.trim()} ${x.n} (${c.ref})`),
              ),
          ),
          gone: list(r.gone),
          lines: list(r.lines.map((k) => placeOf(k))),
          candidates: list(r.candidates.map((k) => placeOf(k))),
        })),
      },
    },
    {
      id: "differences",
      title: "Values that differ from A2000 / Deposco",
      count: differs.length,
      lines: [
        ...byTab(differs),
        ...(["QTY", "FIRST SHIP", "CANCEL"] as const).map(
          (f) => `${f}: ${n(differs.filter((r) => r.diffs.some((d) => d.field === f)).length)}`,
        ),
      ],
      list: {
        title: "Differences",
        columns: [
          ...ROW_COLUMNS,
          { key: "field", label: "Field" },
          { key: "file", label: "File" },
          { key: "app", label: "A2000 / Deposco" },
        ],
        rows: differs.flatMap((r) => r.diffs.map((d) => ({ ...rowOf(r), field: d.field, file: d.file, app: d.app }))),
      },
    },
    {
      id: "problems",
      title: "Text and date problems",
      count: plan.problems.length,
      lines: [...countBy(plan.problems, (p) => `${p.tab.trim()} · ${p.column}`)].map(([k, c]) => `${k}: ${n(c)}`),
      list: {
        title: "Text and date problems",
        columns: [
          { key: "tab", label: "Tab" },
          { key: "n", label: "Excel row" },
          { key: "customer", label: "Customer (file)" },
          { key: "column", label: "Column" },
          { key: "problem", label: "Problem" },
          { key: "value", label: "Value" },
          { key: "dates", label: "Dates in it" },
        ],
        rows: plan.problems.map((p) => ({ ...p, tab: p.tab.trim(), dates: list(p.dates) })),
      },
    },
    {
      id: "unknown",
      title: "List values the app does not know",
      count: plan.unknown.length,
      lines: [...countBy(plan.unknown, (u) => u.list)].map(
        ([l, c]) =>
          `${l}: ${n(c)} (${n(plan.unknown.filter((u) => u.list === l && u.lookAlike).length)} look like a value)`,
      ),
      list: {
        title: "Unknown list values",
        columns: [
          { key: "list", label: "List" },
          { key: "value", label: "Value" },
          { key: "rows", label: "Rows" },
          { key: "lookAlike", label: "Looks like" },
          { key: "where", label: "Where" },
        ],
        rows: plan.unknown.map((u) => ({ ...u, where: list(u.where) })),
      },
    },
    {
      id: "not-in-file",
      title: "Open in the app, in no row of the file",
      count: notIn.length,
      lines: [
        ...[
          ...countBy(
            notIn.filter((r) => r.source === "A2000" && !r.wait),
            (r) => r.wh || "—",
          ),
        ].map(([wh, c]) => `A2000 ${wh}: ${n(c)}`),
        `A2000 with no WMS # yet: ${n(notIn.filter((r) => r.wait === "no-wms").length)} (the sheet gets a row once a WMS # exists)`,
        `Deposco: ${n(deposcoNotIn.length)}${topDeposco.length ? ` — ${list(topDeposco)}` : ""}`,
        "Isaac: is the B2B view too wide, or are Deposco's statuses stale?",
      ],
      list: {
        title: "Open in the app, in no row",
        columns: [
          { key: "source", label: "Source" },
          { key: "ref", label: "PT / SO" },
          { key: "wh", label: "WH" },
          { key: "line", label: "Line" },
          { key: "wait", label: "No line yet" },
          { key: "customerName", label: "Customer" },
          { key: "customerCode", label: "Code" },
          { key: "po", label: "PO" },
        ],
        rows: notIn.map((r) => ({
          ...r,
          wait: r.wait === "no-wms" ? "No WMS #" : r.wait === "group" ? "Waiting for a group" : "",
        })),
      },
    },
    {
      id: "schedule",
      title: "Shipping Schedule",
      count: entries.length,
      lines: [
        `${n(entries.length)} entries under day sections from ${plan.cutoff}`,
        `${n(entries.filter((e) => e.lines.length).length)} match a line · ${n(entries.filter((e) => !e.lines.length).length)} do not (skipped)`,
        `${n(entries.filter((e) => e.outcome === "exists").length)} already saved as a pickup`,
        `${n(plan.carrierTexts.length)} carrier texts the app cannot map (${n(plan.carrierTexts.reduce((s, c) => s + c.entries, 0))} entries)`,
      ],
      list: {
        title: "Shipping Schedule entries",
        columns: [
          { key: "tab", label: "Tab" },
          { key: "n", label: "Excel row" },
          { key: "day", label: "Day" },
          { key: "customer", label: "Customer (file)" },
          { key: "pos", label: "PO#" },
          { key: "refs", label: "PT # / SO #" },
          { key: "matched", label: "Open PT / SO" },
          { key: "lines", label: "Lines" },
          { key: "carrierText", label: "Carrier (file)" },
          { key: "carrier", label: "Carrier (app)" },
          { key: "loadRef", label: "PICK UP # / BOL" },
          { key: "outcome", label: "Outcome" },
        ],
        rows: entries.map((e) => ({
          tab: e.tab.trim(),
          n: e.n,
          day: e.day,
          customer: e.customer,
          pos: list(e.pos),
          refs: list(e.refs),
          matched: list(e.matched),
          lines: list(e.lines.map((k) => placeOf(k))),
          carrierText: e.carrierText,
          carrier: e.carrier ? `${e.carrier.name} (${e.carrier.code})` : "",
          loadRef: e.loadRef,
          outcome: e.outcome === "import" ? "Imports" : e.outcome === "exists" ? "Already saved" : "No line",
        })),
      },
    },
  ];
}

/** A line key in the team's words: `WMS|37092` → "WMS 37092"; a Deposco key → "Deposco PO … · DC …". */
export function placeOf(key: string): string {
  if (key.startsWith("WMS|")) return `WMS ${key.slice(4)}`;
  const [, , po, dc] = key.split("|");
  return ["Deposco", po ? `PO ${po}` : "", dc ? `DC ${dc}` : ""].filter(Boolean).join(" ");
}
